import React, { useRef, useState } from 'react';
import { Icon, Tooltip } from 'antd';
import { useHistory } from 'react-router-dom';
import { capitalizeFirstLetter } from '~helpers/jstool';
import {
  contractStatus,
  contractStatusToWord,
  landlordStatus,
  depositTransferTime,
  rentTransferTime,
} from '~constants/landlord';
import generatePath from '~settings/routing';
import { ExclamationCircle } from 'common/components/svgs';
import { dayFormatType, formatDay } from '~helpers/day';

import EditModal, { inputField } from '../header/edit-modal';
import DifferentiationModal from '../header/differentiation-modal';
import APCategoryEditModal from '../header/ap-category-edit-modal';
import ListingPlanModal from '../header/listing-plan-modal';
import DefaultBookingTypeModal from '../header/default-booking-type-modal';
import useChangeContractStage from '../../useChangeContractStage';
import { camelCaseToUpperFirstCase } from '~client/helpers';
import { isSupplyRole, isAdminRole, isRegionalSupplyHeadRole } from '~client/helpers/auth';

export default function BasicInfo({
  t,
  landlord,
  setPreparedContract,
  updateLandlordLoading,
  getLandlord,
  updateLandlord,
}) {
  const history = useHistory();
  const editModalRef = useRef(null);
  const differentiiationModalRef = useRef(null);
  const defaultBookingTypeModalRef = useRef(null);
  const [showAPCategoryEditModal, setShowAPCategoryEditModal] = useState(false);
  const [showListingPlan, setShowListingPlan] = useState(false);
  const { ChangeContractStageBtnComponent, ChangeContractStageModalComponent } =
    useChangeContractStage({
      landlord: landlord,
      successCallBack: getLandlord,
    });
  const handleClickUploadContract = () => {
    history.push(generatePath('contract', {}));

    setTimeout(() => {
      setPreparedContract(landlord);
    }, 0);
  };

  const handleEditClick = field => {
    editModalRef.current?.handleOpenModal(field);
  };
  const handleDifferentiiation = field => {
    differentiiationModalRef.current?.handleOpenModal(field);
  };

  const handleGoToCheckClick = () => {
    history.push(`/account/account-management?landlordSlugs=${landlord?.slug}`);
  };

  const handleOpenDefaultBookingType = () => {
    defaultBookingTypeModalRef.current?.handleOpenModal(landlord.bookingJourney ? 'edit' : 'add');
  };

  const handleOpenEditModal = () => {
    setShowAPCategoryEditModal(true);
  };

  const handleCancelModal = () => {
    setShowAPCategoryEditModal(false);
  };

  return (
    <div className="landlord-basic-info-wrapper">
      <span className="basic_info-title">{t('cms.landlord.detail.basic_info.tab')}</span>
      <If condition={landlord?.contractStage}>
        <div className="detail-wrapper">
          <div className="title-wrapper">
            <div className="title">{t('cms.landlord.change_contract_stage.title')}</div>
            <If condition={landlord?.contracts?.edges?.length > 0}>
              <div className="edit">{ChangeContractStageBtnComponent}</div>
            </If>
            <If condition={landlord?.contracts?.edges?.length == 0}>
              <Tooltip
                placement="topRight"
                title={t('cms.property.change_contract_stage.edit.tooltip.content')}
              >
                <div className="edit-disable">{t('cms.property_card.action.btn.edit')}</div>
              </Tooltip>
            </If>
          </div>
          <div className="detail-content">
            <div className="status">
              {t(`cms.landlord.contract_stage.${landlord?.contractStage?.toLowerCase()}`)}
            </div>
            <If condition={landlord.status !== landlordStatus.VERIFIED}>
              <div className="tooltip-wrapper">
                <Icon
                  type="exclamation-circle"
                  theme="filled"
                  style={{ color: '#f9a600', marginRight: 6 }}
                />
                <div className="tooltip">
                  {t('cms.landlord.change_contract_stage.tooltip.content')}
                </div>
              </div>
            </If>
          </div>
        </div>
      </If>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{'Account Differentiation'}</div>
          {isRegionalSupplyHeadRole() || isAdminRole() ? (
            <div className="edit" onClick={() => handleDifferentiiation()}>
              {t('cms.property_card.action.btn.edit')}
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="detail-content">
          <div className="status">
            {camelCaseToUpperFirstCase(landlord?.accountDifferentiation?.toLowerCase())}
          </div>
        </div>
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{t('cms.landlord.detail.account_owner.label')}</div>
          <div className="edit" onClick={() => handleEditClick(inputField.ACCOUNT_OWNER)}>
            {t('cms.property_card.action.btn.edit')}
          </div>
        </div>
        <div className="detail-content">
          <div className="status">{landlord.accountManager}</div>
        </div>
      </div>

      <div className="detail-wrapper">
        <div className="title">{t('cms.landlord.detail.property_count.label')}</div>
        <div className="detail-content">
          <div className="status">{landlord.propertiesCount}</div>
        </div>
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{t('cms.landlord.detail.active_attracted_contract.label')}</div>
          <If condition={landlord.contractAttachedStatus === contractStatus.EXPIRED}>
            <div className="edit" onClick={handleClickUploadContract}>
              {t('cms.landlord.detail.go_to_upload.button')}
            </div>
          </If>
        </div>
        <div className="detail-content">
          <div className="status">
            <span>{contractStatusToWord[landlord.contractAttachedStatus]}</span>
          </div>
        </div>
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{t('cms.landlord.detail.users.label')}</div>
          <div className="edit" onClick={handleGoToCheckClick}>
            {t('cms.landlord.detail.go_to_check.button')}
          </div>
        </div>
        <div className="detail-content">
          <div className="status">{landlord.landlordContacts?.totalCount ?? 0}</div>
        </div>
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{t('cms.landlord.detail.default_booking_type.label')}</div>
          <div className="edit" onClick={handleOpenDefaultBookingType}>
            {t(`cms.landlord.detail.${landlord.bookingJourney ? 'edit' : 'add'}.button`)}
          </div>
        </div>
        <div className="detail-content">
          <div className="status">
            {landlord?.bookingJourney
              ? t(`properties_booking_type_${landlord?.bookingJourney?.toLowerCase()}`)
              : '-'}
          </div>
        </div>
        <div className="hint">
          <div>
            <ExclamationCircle className="icon" />
          </div>
          <div className="hintContent">{t('cms.landlord.detail.default_booking_type.hint')}</div>
        </div>
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">Preferred Listing Plan</div>
          <If condition={isSupplyRole() || isAdminRole() || isRegionalSupplyHeadRole()}>
            <div
              className="edit"
              onClick={() => {
                setShowListingPlan(true);
              }}
            >
              {t(`cms.landlord.detail.edit.button`)}
            </div>
          </If>
        </div>
        <div className="detail-content">
          <div className="status">{capitalizeFirstLetter(landlord?.listingPlan) ?? '-'}</div>
        </div>
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{t('cms.landlord.detail.cashflow_management.new_tag')}</div>
          <div className="edit" onClick={handleOpenEditModal}>
            {t('cms.property_card.action.btn.edit')}
          </div>
        </div>
        <div className="detail-content">
          <div className="apcategory-card-wrapper">
            <div className="apcategory-card-title">
              <span>{t('cms.landlord.detail.ap_category.cashflow_management.title')}</span>
              <Tooltip title={t('cms.landlord.detail.ap_category.hint')}>
                <Icon type="question-circle" width={14} height={14} className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="apcategory-wrapper">
              <span>{t('cms.landlord.detail.ap_category.label')}:&nbsp;</span>
              <span>
                {t(
                  `cms.landlord.detail.ap_category.option.${
                    landlord.apCategory?.toLowerCase() ?? apCategoryEnum[0].toLowerCase()
                  }`,
                )}
              </span>
            </div>
            <div className="apcategory-wrapper">
              <span>{t('cms.landlord.detail.ap_category.deposit')}:&nbsp;</span>
              <span>
                {t(
                  `cms.landlord.detail.ap_category.deposit.${
                    landlord?.depositTransferTime?.toLowerCase() ??
                    depositTransferTime[0].toLowerCase()
                  }`,
                )}
              </span>
            </div>
            <div className="apcategory-wrapper">
              <span>{t('cms.landlord.detail.ap_category.rent')}:&nbsp;</span>
              <span>
                {t(
                  `cms.landlord.detail.ap_category.rent.${
                    landlord.rentTransferTime?.toLowerCase() ?? rentTransferTime[0].toLowerCase()
                  }`,
                )}
              </span>
            </div>
          </div>
          <div className="apcategory-card-wrapper" style={{ marginLeft: 16, height: 'auto' }}>
            <div className="apcategory-card-title">
              <span>{t('cms.landlord.detail.ap_category.no_payment_access.title')}</span>
              <Tooltip title={t('cms.landlord.detail.ap_category.no_payment_access.hint')}>
                <Icon type="question-circle" width={14} height={14} className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="apcategory-wrapper">
              {landlord.allowNoPayment ? (
                <Icon
                  type="check"
                  width={14}
                  height={14}
                  style={{ marginRight: 4, color: '#3bc56f' }}
                />
              ) : (
                <Icon
                  type="close"
                  width={14}
                  height={14}
                  style={{ marginRight: 4, color: '#f5222d' }}
                />
              )}
              <span>
                {landlord.allowNoPayment
                  ? t('cms.landlord.detail.ap_category.no_payment_access.allowed')
                  : t('cms.landlord.detail.ap_category.no_payment_access.denied')}
              </span>
            </div>
          </div>
        </div>
        {/*<div className="hint">*/}
        {/*  <div>*/}
        {/*    <ExclamationCircle className="icon" />*/}
        {/*  </div>*/}
        {/*  <div className="hintContent">{t('cms.landlord.detail.ap_category.hint')}</div>*/}
        {/*</div>*/}
      </div>
      <div className="detail-wrapper">
        <div className="title-wrapper">
          <div className="title">{t('cms.landlord.detail.system_info')}</div>
        </div>
        <div
          className="detail-content"
          style={{ flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <div className="apcategory-wrapper">
            <span>{t('cms.landlord.detail.system_info.landlordName')}:&nbsp;</span>
            <span>{landlord.name}</span>
          </div>
          <div className="apcategory-wrapper">
            <span>{t('cms.landlord.detail.system_info.registered_status')}:&nbsp;</span>
            <span>
              {t(`cms.landlord.modal.register_type.${landlord.registerType?.toLowerCase()}.option`)}
            </span>
          </div>
          <div className="apcategory-wrapper">
            <span>{t('cms.landlord.detail.system_info.create_time')}:&nbsp;</span>
            <span>{formatDay(dayFormatType.UKWITHHM, landlord.createdAt)}</span>
          </div>
          <div className="apcategory-wrapper" style={{ marginBottom: 12 }}>
            <span>{t('cms.landlord.detail.system_info.update_time')}:&nbsp;</span>
            <span>{formatDay(dayFormatType.UKWITHHM, landlord.updatedAt)}</span>
          </div>
        </div>
      </div>
      <EditModal
        wrappedComponentRef={form => (editModalRef.current = form)}
        landlord={landlord}
        updateLandlordLoading={updateLandlordLoading}
        updateCallback={getLandlord}
        updateLandlord={updateLandlord}
      />
      <DifferentiationModal
        landlord={landlord}
        wrappedComponentRef={form => (differentiiationModalRef.current = form)}
        updateLandlordLoading={updateLandlordLoading}
        updateCallback={getLandlord}
        updateLandlord={updateLandlord}
      />
      <DefaultBookingTypeModal
        wrappedComponentRef={form => (defaultBookingTypeModalRef.current = form)}
        landlord={landlord}
        updateCallback={getLandlord}
        updateLandlord={updateLandlord}
        updateLandlordLoading={updateLandlordLoading}
      />
      <If condition={showAPCategoryEditModal}>
        <APCategoryEditModal
          showAPCategoryEditModal={showAPCategoryEditModal}
          landlord={landlord}
          updateLandlordLoading={updateLandlordLoading}
          updateCallback={getLandlord}
          updateLandlord={updateLandlord}
          handleCancelModal={handleCancelModal}
        />
      </If>
      <If condition={showListingPlan}>
        <ListingPlanModal
          showListingPlan={showListingPlan}
          landlord={landlord}
          updateLandlordLoading={updateLandlordLoading}
          updateCallback={getLandlord}
          updateLandlord={updateLandlord}
          handleCancelModal={() => {
            setShowListingPlan(false);
          }}
        />
      </If>
      {ChangeContractStageModalComponent}
    </div>
  );
}
